// ScheduleModal.js
// Combines recipient picker (like ForwardModal) + datetime picker in one polished modal.
// • Mobile: full-screen sheet (like ForwardModal)
// • Desktop: centred dialog — does NOT cover the full chat window
//
// Props:
//   users            — DM contact list
//   groups           — group list
//   preSelectedItem  — the currently open chat (selectedItem from ChatWindow).
//                      Pre-ticked automatically so the user can just hit Next.
//   onConfirm(targets, date) — called with selected targets + chosen Date
//   onClose()

import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";

/* ─── tiny helpers ─────────────────────────────────────────────────────────── */
const pad = (n) => String(n).padStart(2, "0");

const toInputValue = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

const minInputValue = () =>
  toInputValue(new Date(Date.now() + 60 * 1000));

const snapToNext30 = () => {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() < 30 ? 30 : 60);
  return d;
};

/** Derive the flat target-id for the currently open chat so we can pre-tick it. */
const preSelectedId = (item) => {
  if (!item) return null;
  if (item.type === "user")  return `user-${item.chatId}`;
  if (item.type === "group") return `group-${item.groupId}`;
  return null;
};

/* ─── icons ─────────────────────────────────────────────────────────────────── */
const ClockIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" />
  </svg>
);

const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="opacity-40">
    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
  </svg>
);

/* ─── STEP INDICATOR ────────────────────────────────────────────────────────── */
function Steps({ step }) {
  const steps = ["Recipients", "Schedule"];
  return (
    <div className="flex items-center gap-0 px-5 py-3">
      {steps.map((label, i) => {
        const active = i + 1 === step;
        const done   = i + 1 < step;
        return (
          <React.Fragment key={label}>
            <div className="flex items-center gap-1.5">
              <div
                className={[
                  "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors",
                  done || active
                    ? "bg-green-600 text-white"
                    : "bg-neutral-200 dark:bg-neutral-700 text-neutral-400",
                ].join(" ")}
              >
                {done ? "✓" : i + 1}
              </div>
              <span
                className={[
                  "text-xs font-medium transition-colors",
                  active
                    ? "text-neutral-900 dark:text-neutral-100"
                    : "text-neutral-400 dark:text-neutral-500",
                ].join(" ")}
              >
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className="flex-1 mx-3 h-px bg-neutral-200 dark:bg-neutral-700" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   ScheduleModal
══════════════════════════════════════════════════════════════════════════════ */
export default function ScheduleModal({
  users           = [],
  groups          = [],
  preSelectedItem = null,   // ← currently open chat; pre-ticked on open
  onConfirm,
  onClose,
}) {
  // ── Derive the pre-selected id once (stable across renders) ──────────────
  const initSelectedId = preSelectedId(preSelectedItem);

  const [step,     setStep]     = useState(1);
  const [query,    setQuery]    = useState("");
  const [selected, setSelected] = useState(() =>
    initSelectedId ? new Set([initSelectedId]) : new Set()
  );
  const [dtValue,  setDtValue]  = useState(() => toInputValue(snapToNext30()));
  const [dtError,  setDtError]  = useState("");

  // We intentionally do NOT auto-focus the search input — that would pop the
  // keyboard open on mobile when the modal appears from a long-press.
  const searchRef = useRef(null);

  /* ── Lock body scroll ── */
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  /* ── On step change: blur any focused element to keep keyboard closed ── */
  useEffect(() => {
    // Actively dismiss keyboard when navigating between steps.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [step]);

  /* ── Build flat list of targets ── */
  const allTargets = useMemo(() => {
  const dm = (users || []).map((u) => ({
  id:     `user-${u.chatId}`,
  type:   "user",
  chatId: u.chatId,
  label:  u.displayName || u.username || String(u.chatId),
  image:  u.profileImageUrl || null,
  online: u.online ?? false,
}));
    const grp = (groups || []).map((g) => ({
      id:      `group-${g.groupId}`,
      type:    "group",
      groupId: g.groupId,
      label:   g.name,
      image:   g.groupImageUrl || null,
    }));
    return [...dm, ...grp];
  }, [users, groups]);

  const filtered = useMemo(() => {
    if (!query.trim()) return allTargets;
    const q = query.toLowerCase();
    return allTargets.filter((t) => t.label.toLowerCase().includes(q));
  }, [allTargets, query]);

  const selectedTargets = allTargets.filter((t) => selected.has(t.id));

  const toggle = useCallback((id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    }), []);

  const handleNext = () => { if (selected.size) setStep(2); };

  const handleConfirm = () => {
    if (!dtValue) { setDtError("Please pick a date and time."); return; }
    const chosen = new Date(dtValue);
    if (isNaN(chosen) || chosen <= new Date()) {
      setDtError("Please pick a future date and time.");
      return;
    }
    const targets = allTargets.filter((t) => selected.has(t.id));
    onConfirm(targets, chosen);
  };

  /* ── Search input focus handler: only focus on explicit tap, not on mount ── */
  const handleSearchFocus = (e) => {
    // Allowed — user tapped the field deliberately.
    // No programmatic focus call here; keyboard shows naturally on tap.
  };

  /* ════════════════════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════════════════════ */
  const content = (
    <div className="flex flex-col h-full min-h-0">

      {/* ── HEADER ── */}
      <div
        className="flex-shrink-0 flex items-center gap-3 px-4 pb-3 border-b border-neutral-200 dark:border-neutral-800"
        style={{ paddingTop: "max(16px, env(safe-area-inset-top, 16px))" }}
      >
        <button
          onClick={step === 2 ? () => setStep(1) : onClose}
          className="flex items-center justify-center w-9 h-9 rounded-full text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors touch-manipulation"
          aria-label={step === 2 ? "Back" : "Close"}
        >
          {step === 2 ? (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          )}
        </button>

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-base text-neutral-900 dark:text-neutral-100 leading-tight">
            Schedule message
          </p>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5 truncate">
            {step === 1
              ? "Choose who receives this message"
              : `Sending to ${selected.size} recipient${selected.size !== 1 ? "s" : ""}`}
          </p>
        </div>

        <div className="flex items-center justify-center w-9 h-9 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 flex-shrink-0">
          <ClockIcon size={18} />
        </div>
      </div>

      {/* ── STEP INDICATOR ── */}
      <Steps step={step} />

      {/* ════════════════════════════════════════════
          STEP 1 — RECIPIENT PICKER
      ════════════════════════════════════════════ */}
      {step === 1 && (
        <>
          {/* Search — readOnly on mobile by default so keyboard stays closed until
              the user deliberately taps to type */}
          <div className="flex-shrink-0 px-4 pb-2">
            <div className="flex items-center gap-2 bg-neutral-100 dark:bg-neutral-800 rounded-full px-4 py-2.5">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="text-neutral-400 flex-shrink-0">
                <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
              <input
                ref={searchRef}
                className="flex-1 bg-transparent text-sm outline-none text-neutral-900 dark:text-neutral-100 placeholder-neutral-400"
                placeholder="Search contacts or groups…"
                value={query}
                onFocus={handleSearchFocus}
                onChange={(e) => setQuery(e.target.value)}
                // No autoFocus — prevents keyboard from popping up on long-press open
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="text-neutral-400 hover:text-neutral-600 touch-manipulation"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Contact list */}
          <div className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: "touch" }}>
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 gap-2 text-neutral-400 text-sm">
                <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" className="opacity-40">
                  <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
                No contacts found
              </div>
            ) : (
              <ul>
                {filtered.map((target) => {
                  const isChecked    = selected.has(target.id);
                  // Highlight the pre-selected current chat so user notices it
                  const isCurrentChat = target.id === initSelectedId;

                  return (
                    <li key={target.id}>
                      <button
                        className={[
                          "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors touch-manipulation",
                          isChecked
                            ? "bg-green-50 dark:bg-green-900/20"
                            : "hover:bg-neutral-50 dark:hover:bg-neutral-800/60 active:bg-neutral-100",
                        ].join(" ")}
                        onClick={() => toggle(target.id)}
                      >
                        {/* Avatar */}
                        <div className="relative w-11 h-11 flex-shrink-0">
                          {target.image ? (
                            <img src={target.image} alt={target.label} className="w-11 h-11 rounded-full object-cover" />
                          ) : (
                            <div
                              className="w-11 h-11 rounded-full flex items-center justify-center text-lg"
                              style={{ background: target.type === "group" ? "#d1fae5" : "#e0f2fe" }}
                            >
                              {target.type === "group" ? "👥" : "👤"}
                            </div>
                          )}
                          {target.type === "user" && (
                            <span
                              className={[
                                "absolute bottom-0.5 right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-neutral-900",
                                target.online ? "bg-green-500" : "bg-neutral-300 dark:bg-neutral-600",
                              ].join(" ")}
                            />
                          )}
                        </div>

                        {/* Name + "current chat" badge */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <p className="font-medium text-sm truncate text-neutral-900 dark:text-neutral-100">
                              {target.label}
                            </p>
                            {isCurrentChat && (
                              <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
                                current
                              </span>
                            )}
                          </div>
                          {target.type === "group" && (
                            <p className="text-xs text-neutral-400 mt-0.5">Group</p>
                          )}
                        </div>

                        {/* Tick */}
                        <div
                          className={[
                            "w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all",
                            isChecked
                              ? "bg-green-600 border-green-600 scale-110"
                              : "border-neutral-300 dark:border-neutral-600",
                          ].join(" ")}
                        >
                          {isChecked && (
                            <svg viewBox="0 0 24 24" width="13" height="13" fill="white">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div
            className="flex-shrink-0 border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-4 py-3"
            style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom, 12px))" }}
          >
            {/* Selected chips */}
            {selectedTargets.length > 0 && (
              <div
                className="flex gap-2 overflow-x-auto pb-2 mb-3"
                style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}
              >
                {selectedTargets.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => toggle(t.id)}
                    className="flex-shrink-0 flex items-center gap-1.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full pl-2 pr-3 py-1.5 text-xs font-medium touch-manipulation hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-700 dark:hover:text-red-300 transition-colors group"
                  >
                    <span>{t.type === "group" ? "👥" : "👤"}</span>
                    <span className="max-w-[80px] truncate">{t.label}</span>
                    <svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor" className="opacity-60 group-hover:opacity-100">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-sm text-neutral-500 dark:text-neutral-400">
                {selected.size > 0
                  ? `${selected.size} selected`
                  : "Select at least one"}
              </span>
              <button
                disabled={!selected.size}
                onClick={handleNext}
                className={[
                  "flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white transition-all touch-manipulation",
                  selected.size
                    ? "bg-green-600 hover:bg-green-700 active:bg-green-800 shadow-sm"
                    : "bg-neutral-300 dark:bg-neutral-700 cursor-not-allowed opacity-60",
                ].join(" ")}
              >
                Next
                <ChevronIcon />
              </button>
            </div>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════
          STEP 2 — DATE / TIME PICKER
      ════════════════════════════════════════════ */}
      {step === 2 && (
        <>
          <div className="flex-1 px-4 py-4 flex flex-col gap-4 overflow-y-auto">

            {/* Recipients summary */}
            <div className="rounded-xl bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 px-4 py-3">
              <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-2">
                Sending to
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedTargets.map((t) => (
                  <span
                    key={t.id}
                    className="inline-flex items-center gap-1.5 bg-white dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-full pl-2 pr-3 py-1 text-xs font-medium text-neutral-700 dark:text-neutral-200 shadow-sm"
                  >
                    {t.image ? (
                      <img src={t.image} alt={t.label} className="w-4 h-4 rounded-full object-cover" />
                    ) : (
                      <span className="text-sm">{t.type === "group" ? "👥" : "👤"}</span>
                    )}
                    <span className="max-w-[100px] truncate">{t.label}</span>
                  </span>
                ))}
              </div>
            </div>

            {/* Datetime picker */}
            <div>
              <label className="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-2">
                Send at
              </label>
              <input
                type="datetime-local"
                className={[
                  "w-full rounded-xl border px-4 py-3 text-sm",
                  "bg-white dark:bg-neutral-800",
                  "text-neutral-900 dark:text-neutral-100",
                  "focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent",
                  "transition-colors",
                  dtError
                    ? "border-red-400 dark:border-red-500"
                    : "border-neutral-200 dark:border-neutral-700",
                ].join(" ")}
                value={dtValue}
                min={minInputValue()}
                onChange={(e) => { setDtValue(e.target.value); setDtError(""); }}
              />
              {dtError && (
                <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                  {dtError}
                </p>
              )}

              {/* Quick-pick shortcuts */}
              <div className="mt-3 flex flex-wrap gap-2">
                {[
                  { label: "In 1 hour",    offset: 1  * 3600 * 1000 },
                  { label: "In 3 hours",   offset: 3  * 3600 * 1000 },
                  { label: "Tomorrow 9am", special: "tomorrow9" },
                  { label: "In 1 day",     offset: 24 * 3600 * 1000 },
                ].map(({ label, offset, special }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => {
                      let d;
                      if (special === "tomorrow9") {
                        d = new Date();
                        d.setDate(d.getDate() + 1);
                        d.setHours(9, 0, 0, 0);
                      } else {
                        d = new Date(Date.now() + offset);
                        d.setSeconds(0, 0);
                      }
                      setDtValue(toInputValue(d));
                      setDtError("");
                    }}
                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 hover:bg-green-100 dark:hover:bg-green-900/30 hover:text-green-700 dark:hover:text-green-300 transition-colors touch-manipulation border border-neutral-200 dark:border-neutral-600"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Info note */}
            <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800 px-4 py-3">
              <ClockIcon size={16} />
              <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                Your message will be sent automatically at the chosen time. You can cancel it from the chat before it sends.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div
            className="flex-shrink-0 border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-4 py-3"
            style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom, 12px))" }}
          >
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 rounded-full text-sm font-medium text-neutral-600 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors touch-manipulation"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="flex-[2] py-2.5 rounded-full text-sm font-semibold text-white bg-green-600 hover:bg-green-700 active:bg-green-800 transition-colors touch-manipulation flex items-center justify-center gap-2 shadow-sm"
              >
                <ClockIcon size={15} />
                Schedule send
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  /* ── WRAPPER: full-screen mobile / centred card desktop ─────────────────── */
  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Card */}
      <div
        className={[
          "fixed z-[70]",
          "inset-x-0 bottom-0 top-0",
          "sm:inset-auto sm:top-1/2 sm:left-1/2",
          "sm:-translate-x-1/2 sm:-translate-y-1/2",
          "sm:w-[460px] sm:max-h-[88vh] sm:rounded-2xl",
          "bg-white dark:bg-neutral-900",
          "sm:shadow-2xl sm:border sm:border-neutral-200 sm:dark:border-neutral-700",
          "flex flex-col overflow-hidden",
        ].join(" ")}
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </div>
    </>
  );
}